/**
 * useImageGeneration Hook
 *
 * Manages image generation state and workflow for a post.
 * Handles multi-step process: prompt generation → image generation → display.
 *
 * React 19 patterns:
 * - useReducer for complex state (6+ related states)
 * - useCallback for event handlers
 * - useMemo for derived values
 * - No console.log statements
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useAction, useQuery } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';

// ============================================
// STATE TYPES
// ============================================

interface ImageGenerationState {
  // Current step in workflow
  step: 'idle' | 'generating_prompt' | 'generating_image' | 'completed' | 'error';

  // Generated artifacts
  promptId: Id<'imagePrompts'> | null;
  promptText: string | null;
  imageId: Id<'images'> | null;
  imageUrl: string | null;

  // Image metadata
  width: number | null;
  height: number | null;
  fileSize: number | null;

  // Error state
  error: string | null;
  errorCode: string | null;
  failedStep: 'prompt' | 'image' | null;

  // User input
  customPrompt: string;
  selectedAspectRatio: '1:1' | '4:5' | '9:16' | '16:9';
}

type ImageGenerationAction =
  | { type: 'START_GENERATION' }
  | { type: 'PROMPT_SUCCESS'; payload: { promptId: Id<'imagePrompts'>; promptText: string } }
  | {
      type: 'IMAGE_SUCCESS';
      payload: {
        imageId: Id<'images'>;
        imageUrl: string;
        width: number;
        height: number;
        fileSize: number;
      };
    }
  | { type: 'ERROR'; payload: { error: string; errorCode?: string; step: 'prompt' | 'image' } }
  | { type: 'SET_CUSTOM_PROMPT'; payload: string }
  | { type: 'SET_ASPECT_RATIO'; payload: '1:1' | '4:5' | '9:16' | '16:9' }
  | { type: 'RESET' }
  | { type: 'RETRY_FROM_PROMPT' };

// ============================================
// REDUCER
// ============================================

const initialState: ImageGenerationState = {
  step: 'idle',
  promptId: null,
  promptText: null,
  imageId: null,
  imageUrl: null,
  width: null,
  height: null,
  fileSize: null,
  error: null,
  errorCode: null,
  failedStep: null,
  customPrompt: '',
  selectedAspectRatio: '9:16',
};

function imageGenerationReducer(
  state: ImageGenerationState,
  action: ImageGenerationAction
): ImageGenerationState {
  switch (action.type) {
    case 'START_GENERATION':
      return {
        ...state,
        step: 'generating_prompt',
        error: null,
        errorCode: null,
        failedStep: null,
      };

    case 'PROMPT_SUCCESS':
      return {
        ...state,
        step: 'generating_image',
        promptId: action.payload.promptId,
        promptText: action.payload.promptText,
      };

    case 'IMAGE_SUCCESS':
      return {
        ...state,
        step: 'completed',
        imageId: action.payload.imageId,
        imageUrl: action.payload.imageUrl,
        width: action.payload.width,
        height: action.payload.height,
        fileSize: action.payload.fileSize,
      };

    case 'ERROR':
      return {
        ...state,
        step: 'error',
        error: action.payload.error,
        errorCode: action.payload.errorCode || null,
        failedStep: action.payload.step,
      };

    case 'SET_CUSTOM_PROMPT':
      return {
        ...state,
        customPrompt: action.payload,
      };

    case 'SET_ASPECT_RATIO':
      return {
        ...state,
        selectedAspectRatio: action.payload,
      };

    case 'RESET':
      return initialState;

    case 'RETRY_FROM_PROMPT':
      return {
        ...state,
        step: 'generating_image',
        error: null,
        errorCode: null,
        failedStep: null,
      };

    default:
      return state;
  }
}

// ============================================
// HOOK
// ============================================

export function useImageGeneration({
  postId,
  onSuccess,
  onError,
}: {
  postId: Id<'posts'>;
  onSuccess?: (imageId: Id<'images'>, imageUrl: string) => void;
  onError?: (error: string, step: 'prompt' | 'image') => void;
}) {
  // ============================================
  // STATE
  // ============================================
  const [state, dispatch] = useReducer(imageGenerationReducer, initialState);

  // ============================================
  // CONVEX QUERIES
  // ============================================

  // Fetch existing prompt for this post (skip if no valid postId)
  const existingPrompt = useQuery(
    api.ai.queries.getLatestPromptByPost,
    postId ? { postId } : 'skip'
  );

  // Fetch existing image for this post (skip if no valid postId)
  const existingImage = useQuery(api.ai.queries.getLatestImageByPost, postId ? { postId } : 'skip');

  // Sync aspect ratio from existing image (only once when data first loads).
  // Moved into an effect to avoid reading/writing ref.current during render.
  const aspectRatioSyncedRef = useRef(false);
  useEffect(() => {
    if (existingImage?.aspectRatio && !aspectRatioSyncedRef.current) {
      aspectRatioSyncedRef.current = true;
      dispatch({
        type: 'SET_ASPECT_RATIO',
        payload: existingImage.aspectRatio as '1:1' | '4:5' | '9:16' | '16:9', // AS_JUSTIFIED: aspectRatio from Convex is v.string(); known values match the union
      });
    }
  }, [existingImage?.aspectRatio]);

  // ============================================
  // CONVEX ACTIONS
  // ============================================

  const generateCompleteImage = useAction(api.ai.orchestration.generateCompleteImage);
  const regenerateImage = useAction(api.ai.orchestration.regenerateImage);
  const generateWithCustomPrompt = useAction(api.ai.orchestration.generateImageWithCustomPrompt);
  const regeneratePrompt = useAction(api.ai.orchestration.regeneratePrompt);

  // ============================================
  // COMPUTED VALUES
  // ============================================

  const hasExistingPrompt = useMemo(() => !!existingPrompt, [existingPrompt]);
  const hasExistingImage = useMemo(() => !!existingImage, [existingImage]);

  const isGenerating = useMemo(
    () => state.step === 'generating_prompt' || state.step === 'generating_image',
    [state.step]
  );

  const canRetry = useMemo(
    () => state.step === 'error' && state.failedStep !== null,
    [state.step, state.failedStep]
  );

  const currentImageUrl = useMemo(
    () => state.imageUrl || existingImage?.imageUrl || null,
    [state.imageUrl, existingImage]
  );

  const currentPromptText = useMemo(
    () => state.promptText || existingPrompt?.promptText || null,
    [state.promptText, existingPrompt]
  );

  // ============================================
  // EVENT HANDLERS
  // ============================================

  /**
   * Generate complete image (prompt + image)
   */
  const handleGenerate = useCallback(
    async (aspectRatioOverride?: '1:1' | '4:5' | '9:16' | '16:9') => {
      dispatch({ type: 'START_GENERATION' });

      // Use override if provided, otherwise fall back to state
      const aspectRatioToUse = aspectRatioOverride ?? state.selectedAspectRatio;

      // If using override, update state to match what will be generated
      if (aspectRatioOverride && aspectRatioOverride !== state.selectedAspectRatio) {
        dispatch({ type: 'SET_ASPECT_RATIO', payload: aspectRatioOverride });
      }

      try {
        const result = await generateCompleteImage({
          postId,
          aspectRatio: aspectRatioToUse,
        });

        if (!result.success) {
          dispatch({
            type: 'ERROR',
            payload: {
              error: result.error || 'Generation failed',
              errorCode: result.errorCode,
              step: result.step as 'prompt' | 'image',
            },
          });
          toast.error('Image generation failed', {
            description: result.error || 'Generation failed',
          });
          onError?.(result.error || 'Generation failed', result.step as 'prompt' | 'image');
          return;
        }

        // Prompt generated successfully
        if (result.promptId && result.promptText) {
          dispatch({
            type: 'PROMPT_SUCCESS',
            payload: {
              promptId: result.promptId,
              promptText: result.promptText,
            },
          });
        }

        // Image generated successfully
        if (result.imageId && result.imageUrl) {
          dispatch({
            type: 'IMAGE_SUCCESS',
            payload: {
              imageId: result.imageId,
              imageUrl: result.imageUrl,
              width: result.width || 0,
              height: result.height || 0,
              fileSize: result.fileSize || 0,
            },
          });
          toast.success('Image generated successfully!', {
            description: 'Your AI-generated image is ready to use.',
          });
          onSuccess?.(result.imageId, result.imageUrl);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        dispatch({
          type: 'ERROR',
          payload: {
            error: errorMessage,
            step: 'prompt',
          },
        });
        toast.error('Image generation failed', {
          description: errorMessage,
        });
        onError?.(errorMessage, 'prompt');
      }
    },
    [postId, state.selectedAspectRatio, generateCompleteImage, onSuccess, onError]
  );

  /**
   * Generate image with custom prompt
   */
  const handleGenerateWithCustomPrompt = useCallback(async () => {
    if (!state.customPrompt.trim()) {
      dispatch({
        type: 'ERROR',
        payload: {
          error: 'Please enter a custom prompt',
          step: 'prompt',
        },
      });
      return;
    }

    dispatch({ type: 'START_GENERATION' });

    try {
      const result = await generateWithCustomPrompt({
        postId,
        promptText: state.customPrompt,
        aspectRatio: state.selectedAspectRatio,
      });

      if (!result.success) {
        dispatch({
          type: 'ERROR',
          payload: {
            error: result.error || 'Generation failed',
            errorCode: result.errorCode,
            step: 'image',
          },
        });
        toast.error('Image generation failed', {
          description: result.error || 'Generation failed',
        });
        onError?.(result.error || 'Generation failed', 'image');
        return;
      }

      // Success
      if (result.imageId && result.imageUrl && result.promptId) {
        dispatch({
          type: 'PROMPT_SUCCESS',
          payload: {
            promptId: result.promptId,
            promptText: state.customPrompt,
          },
        });
        dispatch({
          type: 'IMAGE_SUCCESS',
          payload: {
            imageId: result.imageId,
            imageUrl: result.imageUrl,
            width: result.width || 0,
            height: result.height || 0,
            fileSize: result.fileSize || 0,
          },
        });
        onSuccess?.(result.imageId, result.imageUrl);
      }
    } catch (error) {
      dispatch({
        type: 'ERROR',
        payload: {
          error: error instanceof Error ? error.message : 'Unknown error',
          step: 'image',
        },
      });
      onError?.(error instanceof Error ? error.message : 'Unknown error', 'image');
    }
  }, [
    state.customPrompt,
    state.selectedAspectRatio,
    postId,
    generateWithCustomPrompt,
    onSuccess,
    onError,
  ]);

  /**
   * Generate image with prompt passed directly (bypasses state race condition)
   */
  const handleGenerateWithPrompt = useCallback(
    async (promptText: string) => {
      if (!promptText.trim()) {
        dispatch({
          type: 'ERROR',
          payload: {
            error: 'Please enter a prompt',
            step: 'prompt',
          },
        });
        return;
      }

      dispatch({ type: 'START_GENERATION' });

      try {
        const result = await generateWithCustomPrompt({
          postId,
          promptText: promptText,
          aspectRatio: state.selectedAspectRatio,
        });

        if (!result.success) {
          dispatch({
            type: 'ERROR',
            payload: {
              error: result.error || 'Generation failed',
              errorCode: result.errorCode,
              step: 'image',
            },
          });
          toast.error('Image generation failed', {
            description: result.error || 'Generation failed',
          });
          onError?.(result.error || 'Generation failed', 'image');
          return;
        }

        // Success
        if (result.imageId && result.imageUrl && result.promptId) {
          dispatch({
            type: 'PROMPT_SUCCESS',
            payload: {
              promptId: result.promptId,
              promptText: promptText,
            },
          });
          dispatch({
            type: 'IMAGE_SUCCESS',
            payload: {
              imageId: result.imageId,
              imageUrl: result.imageUrl,
              width: result.width || 0,
              height: result.height || 0,
              fileSize: result.fileSize || 0,
            },
          });
          toast.success('Image regenerated successfully!', {
            description: 'Your AI-generated image is ready.',
          });
          onSuccess?.(result.imageId, result.imageUrl);
        }
      } catch (error) {
        dispatch({
          type: 'ERROR',
          payload: {
            error: error instanceof Error ? error.message : 'Unknown error',
            step: 'image',
          },
        });
        toast.error('Image generation failed', {
          description: error instanceof Error ? error.message : 'Unknown error',
        });
        onError?.(error instanceof Error ? error.message : 'Unknown error', 'image');
      }
    },
    [postId, state.selectedAspectRatio, generateWithCustomPrompt, onSuccess, onError]
  );

  /**
   * Regenerate image with existing prompt
   */
  const handleRegenerate = useCallback(async () => {
    if (!state.promptId && !existingPrompt?._id) {
      dispatch({
        type: 'ERROR',
        payload: {
          error: 'No prompt available to regenerate from',
          step: 'image',
        },
      });
      return;
    }

    const promptId = state.promptId || existingPrompt?._id;
    if (!promptId) return;

    dispatch({ type: 'RETRY_FROM_PROMPT' });

    try {
      const result = await regenerateImage({
        promptId,
        aspectRatio: state.selectedAspectRatio,
      });

      if (!result.success) {
        dispatch({
          type: 'ERROR',
          payload: {
            error: result.error || 'Regeneration failed',
            errorCode: result.errorCode,
            step: 'image',
          },
        });
        toast.error('Image regeneration failed', {
          description: result.error || 'Regeneration failed',
        });
        onError?.(result.error || 'Regeneration failed', 'image');
        return;
      }

      // Success
      if (result.imageId && result.imageUrl) {
        dispatch({
          type: 'IMAGE_SUCCESS',
          payload: {
            imageId: result.imageId,
            imageUrl: result.imageUrl,
            width: result.width || 0,
            height: result.height || 0,
            fileSize: result.fileSize || 0,
          },
        });
        onSuccess?.(result.imageId, result.imageUrl);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      dispatch({
        type: 'ERROR',
        payload: {
          error: errorMessage,
          step: 'image',
        },
      });
      toast.error('Image regeneration failed', {
        description: errorMessage,
      });
      onError?.(errorMessage, 'image');
    }
  }, [
    state.promptId,
    state.selectedAspectRatio,
    existingPrompt,
    regenerateImage,
    onSuccess,
    onError,
  ]);

  /**
   * Regenerate prompt only (without generating image)
   */
  const handleRegeneratePrompt = useCallback(async () => {
    dispatch({ type: 'START_GENERATION' });

    try {
      const result = await regeneratePrompt({
        postId,
      });

      if (!result.success) {
        dispatch({
          type: 'ERROR',
          payload: {
            error: result.error || 'Prompt generation failed',
            errorCode: result.errorCode,
            step: 'prompt',
          },
        });
        toast.error('Prompt generation failed', {
          description: result.error || 'Prompt generation failed',
        });
        onError?.(result.error || 'Prompt generation failed', 'prompt');
        return;
      }

      // Success - stop at prompt, don't generate image
      if (result.promptId && result.promptText) {
        dispatch({
          type: 'PROMPT_SUCCESS',
          payload: {
            promptId: result.promptId,
            promptText: result.promptText,
          },
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      dispatch({
        type: 'ERROR',
        payload: {
          error: errorMessage,
          step: 'prompt',
        },
      });
      toast.error('Prompt generation failed', {
        description: errorMessage,
      });
      onError?.(errorMessage, 'prompt');
    }
  }, [postId, regeneratePrompt, onError]);

  /**
   * Set custom prompt text
   */
  const handleSetCustomPrompt = useCallback((prompt: string) => {
    dispatch({ type: 'SET_CUSTOM_PROMPT', payload: prompt });
  }, []);

  /**
   * Set aspect ratio
   */
  const handleSetAspectRatio = useCallback((ratio: '1:1' | '4:5' | '9:16' | '16:9') => {
    dispatch({ type: 'SET_ASPECT_RATIO', payload: ratio });
  }, []);

  /**
   * Reset state
   */
  const handleReset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  // ============================================
  // RETURN API
  // ============================================

  return {
    // State
    state,
    step: state.step,
    error: state.error,
    errorCode: state.errorCode,
    failedStep: state.failedStep,
    customPrompt: state.customPrompt,
    selectedAspectRatio: state.selectedAspectRatio,

    // Generated artifacts
    promptId: state.promptId,
    promptText: currentPromptText,
    imageId: state.imageId,
    imageUrl: currentImageUrl,
    width: state.width,
    height: state.height,
    fileSize: state.fileSize,

    // Existing data
    existingPrompt,
    existingImage,
    hasExistingPrompt,
    hasExistingImage,

    // Derived state
    isGenerating,
    canRetry,

    // Actions
    generate: handleGenerate,
    generateWithCustomPrompt: handleGenerateWithCustomPrompt,
    generateWithPrompt: handleGenerateWithPrompt,
    regenerate: handleRegenerate,
    regeneratePrompt: handleRegeneratePrompt,
    setCustomPrompt: handleSetCustomPrompt,
    setAspectRatio: handleSetAspectRatio,
    reset: handleReset,
  };
}
